<script>
import { useCurrencyInput } from "vue-currency-input";
import { watch } from 'vue'
import { watchDebounced } from "@vueuse/core";

export default {
  name: "NumInput",
  // Options you can pass to this component: - Refer to https://dm4t2.github.io/vue-currency-input/config.html

  // --> currency
  // An ISO 4217 currency code, for example "USD" or "EUR". This option is required.
  // https://en.wikipedia.org/wiki/ISO_4217
  // AUD, EUR, USD, EUR, JPY, GBP, BRL, INR, CNY

  // --> locale
  // A BCP 47 language tag (for example "en" or "de-DE"). Default is undefined (use the default locale of the Browser).
  // en-AU, en-US, etc

  // --> currencyDisplay
  // How to display the currency in currency formatting. Possible values are:
  // "symbol" to use a localized currency symbol such as "€" (default value)
  // "narrowSymbol" to use a narrow format symbol ("$100" rather than "US$100")
  // "code" to use the ISO currency code
  // "name" to use a localized currency name such as "dollar"
  // "hidden" to hide the currency
  
  // --> accountingSign
  // Whether to use accounting sign formatting, for example wrapping negative values with parentheses instead of prepending a minus sign.

  // --> autoDecimalDigits
  // Whether the decimal symbol is inserted automatically, using the last inputted digits as decimal digits. Default is false (the decimal symbol needs to be inserted manually).

  // --> precision
  // The number of displayed decimal digits. Default is undefined (use the currency's default, decimal digits will be hidden for integer numbers). Must be between 0 and 15 and can only be applied for currencies that support decimal digits. You can also pass an object {min, max} to use a precision range.

  // --> hideCurrencySymbolOnFocus
  // Whether to hide the currency symbol on focus. Default is true.

  // --> hideGroupingSeparatorOnFocus
  // Whether to hide the grouping separator on focus. Default is true.

  // --> hideNegligibleDecimalDigitsOnFocus
  // Whether to hide negligible decimal digits on focus. Default is true.

  // --> valueScaling
  // Applies a scaling to the exported value. Possible values are:

  // "precision" for scaling float values automatically to integers depending on the current precision, for example 1.23 -> 123
  // "thounsands" for using a scaling factor of 1,000
  // "millions"" for using scaling factor of 1,000,000
  // "billions" for using a scaling factor of 1,000,000,000
  
  // --> valueRange
  // The range of accepted values as object {min, max}. Default is undefined (no value range). The validation is triggered on blur and automatically sets the respective threshold if out of range.

  // --> useGrouping
  // Whether to use grouping separators such as thousands/lakh/crore separators.

  props: {
    modelValue: {
      type: [Number, null],
      required: true
    },

    options: {
      type: Object,
      required: false
    },

    name: {
      type: String,
      default: 'inputField',
      required: false
    },

    label: {
      type: String,
      default: '',
      required: false
    },

    placeholder: {
      type: String,
      default: '',
      required: false
    },

    cssClass: {
      type: String,
      default: '',
      required: false
    },
  },

  setup (props, { emit }) {
    const defaultOptions = {
      locale: 'en-AU',
      currency: 'AUD',
      currencyDisplay: 'symbol',
      hideCurrencySymbolOnFocus: false,
      hideGroupingSeparatorOnFocus: true,
      hideNegligibleDecimalDigitsOnFocus: false,
      precision: 2,
      valueRange: {min: 1, max: 500000}
    }

    // eslint-disable-next-line no-unused-vars
    const {inputRef, numberValue, formattedValue, setValue, setOptions} = useCurrencyInput(
      {...defaultOptions, ...props.options},
      false
    );

    watch(() => props.modelValue, (value) => {
      setValue(value);
    });
    
    watch(() => props.options, (value) => {
      setOptions({...defaultOptions, ...value});
      emit("update:options", value);
    });

    watchDebounced(numberValue, (value) => emit("update:modelValue", value), {
      debounce: 1000,
    });

    return { inputRef };
  }
};
</script>

<template>
  <div class="form-floating mb-3">
    <input 
      tabindex="0"
      ref="inputRef" 
      type="text" 
      class="form-control mini-input num-input" 
      :class="cssClass" 
      :id="name"
      :name="name"
      :placeholder="placeholder"
    />
    <label :for="name">
      {{ label }}
    </label>
  </div>
</template>
